import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, MessagesSquare, ArrowLeft } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gateAgreementFromError } from "@/lib/gate";
import AgreementSignModal from "@/components/AgreementSignModal";
import { toast } from "sonner";

const Avatar = ({ name, avatar, size = 40 }) =>
  avatar ? <img src={avatar} alt="" style={{ height: size, width: size }} className="rounded-full object-cover" />
    : <span style={{ height: size, width: size }} className="rounded-full bg-[#144F75]/10 text-[#144F75] grid place-items-center font-semibold">{(name || "?").charAt(0).toUpperCase()}</span>;

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function Messages() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("to") || "";
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [contactPhone, setContactPhone] = useState("");
  const [agreement, setAgreement] = useState(null);
  const scrollRef = useRef(null);

  const { data: convData, isLoading: convLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.get("/messages/conversations").then((r) => r.data.conversations),
    enabled: !!user,
    refetchInterval: 20000,
  });

  const { data: thread, isLoading: threadLoading } = useQuery({
    queryKey: ["thread", activeId],
    queryFn: () => api.get(`/messages/with/${activeId}`).then((r) => r.data),
    enabled: !!activeId,
    refetchInterval: activeId ? 8000 : false,
  });

  useEffect(() => {
    if (thread) {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["unread-count"] });
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    }
  }, [thread, qc]);

  const isNewThread = !!activeId && !threadLoading && (thread?.messages || []).length === 0;

  const send = async () => {
    if (!text.trim() || !activeId) return;
    if (isNewThread && !contactEmail.trim()) { toast.error("Please add a contact email so they can follow up."); return; }
    setSending(true);
    try {
      await api.post(`/messages/with/${activeId}`, {
        body: text.trim(),
        contact_email: isNewThread ? contactEmail.trim() : "",
        contact_phone: isNewThread ? contactPhone.trim() : "",
      });
      setText("");
      qc.invalidateQueries({ queryKey: ["thread", activeId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    } catch (e) {
      const ag = await gateAgreementFromError(e);
      if (ag) setAgreement(ag);
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setSending(false); }
  };

  const openThread = (id) => setParams({ to: id });

  return (
    <div data-testid="messages-page" className="max-w-[1100px] mx-auto px-4 lg:px-10 py-8">
      <h1 className="font-serif text-3xl text-[#1A242B] mb-6 flex items-center gap-2"><MessagesSquare size={24} className="text-[#144F75]" /> Messages</h1>
      <div className="eyw-card overflow-hidden grid md:grid-cols-[320px_1fr] min-h-[60vh]">
        {/* Conversation list */}
        <div className={`border-r border-[#E5E0D8] ${activeId ? "hidden md:block" : "block"}`} data-testid="conversation-list">
          {convLoading ? (
            <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
          ) : (convData || []).length === 0 ? (
            <div className="p-8 text-center text-[#4A5D6B] text-sm">
              No conversations yet. Visit the <Link to="/community/members" className="text-[#144F75] underline">member directory</Link> to start one.
            </div>
          ) : (
            <ul>
              {convData.map((c) => (
                <li key={c.pair_key}>
                  <button onClick={() => openThread(c.other.id)} data-testid={`conversation-${c.other.id}`}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#E5E0D8]/60 hover:bg-[#144F75]/[0.03] ${activeId === c.other.id ? "bg-[#144F75]/[0.05]" : ""}`}>
                    <Avatar name={c.other.name} avatar={c.other.avatar} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#1A242B] font-medium truncate">{c.other.name}</span>
                        <span className="text-[10px] text-[#8FA89B] shrink-0">{timeAgo(c.last_at)}</span>
                      </div>
                      {c.other.guest_email && <p className="text-[11px] text-[#8FA89B] truncate" data-testid={`conversation-email-${c.other.id}`}>{c.other.guest_email}</p>}
                      {c.subject && (
                        <p className="text-xs font-medium text-[#144F75] truncate" data-testid={`conversation-subject-${c.other.id}`}>
                          {c.kind === "booking" ? "Booking · " : c.kind === "contact" ? "Contact · " : ""}{c.subject.replace(/^Booking: /, "")}
                        </p>
                      )}
                      <p className="text-xs text-[#4A5D6B] truncate">{c.last_body}</p>
                      {c.received_at && <p className="text-[10px] text-[#8FA89B]" data-testid={`conversation-received-${c.other.id}`}>Received {fmtDateTime(c.received_at)}</p>}
                    </div>
                    {c.unread > 0 && <span className="ml-1 bg-[#D98E73] text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 grid place-items-center shrink-0" data-testid={`conversation-unread-${c.other.id}`}>{c.unread}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread */}
        <div className={`flex flex-col ${activeId ? "flex" : "hidden md:flex"}`}>
          {!activeId ? (
            <div className="flex-1 grid place-items-center text-[#8FA89B] text-sm p-8">Select a conversation to start chatting.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E5E0D8] flex items-center gap-3">
                <button onClick={() => setParams({})} className="md:hidden text-[#4A5D6B]" data-testid="thread-back"><ArrowLeft size={18} /></button>
                <Avatar name={thread?.other?.name} avatar={thread?.other?.avatar} size={34} />
                {thread?.is_guest_inquiry ? (
                  <div data-testid="thread-title">
                    <span className="font-medium text-[#1A242B]">{thread?.other?.name || "Guest"}</span>
                    {thread?.other?.guest_email && <span className="block text-xs text-[#8FA89B]">{thread.other.guest_email}</span>}
                    {thread?.subject && <span className="block text-xs text-[#144F75]" data-testid="thread-subject">{thread.subject}{thread.received_at ? ` · received ${fmtDateTime(thread.received_at)}` : ""}</span>}
                  </div>
                ) : (
                  <Link to={`/community/members/${activeId}`} className="font-medium text-[#1A242B] hover:text-[#144F75]" data-testid="thread-title">{thread?.other?.name || "Member"}</Link>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[52vh]" data-testid="thread-messages">
                {threadLoading ? (
                  <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
                ) : (thread?.messages || []).length === 0 ? (
                  <p className="text-center text-[#8FA89B] text-sm py-10">Say hello 👋</p>
                ) : (
                  thread.messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div data-testid={`message-${m.id}`} className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#144F75] text-white rounded-br-sm" : "bg-[#F4F1EB] text-[#1A242B] rounded-bl-sm"}`}>
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          {(m.contact_email || m.contact_phone) && (
                            <p className={`flex flex-wrap gap-x-3 text-[11px] mt-1.5 ${mine ? "text-white/75" : "text-[#4A5D6B]"}`} data-testid={`message-contact-${m.id}`}>
                              {m.contact_email && <span>✉ {m.contact_email}</span>}
                              {m.contact_phone && <span>☎ {m.contact_phone}</span>}
                            </p>
                          )}
                          <span className={`block text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#8FA89B]"}`}>{timeAgo(m.created_at)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-[#E5E0D8] p-3 space-y-2">
                {isNewThread && (
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="new-thread-contact">
                    <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} type="email" required placeholder="Contact email *" data-testid="thread-contact-email" className="eyw-input !py-1.5 text-sm" />
                    <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Callback phone (optional)" data-testid="thread-contact-phone" className="eyw-input !py-1.5 text-sm" />
                  </div>
                )}
                <div className="flex gap-2">
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                    data-testid="message-input" placeholder="Write a message…" className="eyw-input flex-1" />
                  <button onClick={send} disabled={sending || !text.trim()} data-testid="message-send-btn" className="eyw-btn !px-4 disabled:opacity-50">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {agreement && <AgreementSignModal agreement={agreement} onClose={() => setAgreement(null)} onSigned={() => { setAgreement(null); send(); }} />}
    </div>
  );
}
